import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api';
import type { GitProvider, ProjectRepositories } from '../../api';
import { toItems } from './useGitRepositories';
import type { RepositoryItem, RepositorySource } from './useGitRepositories';

export interface ProjectRepositorySource extends RepositorySource {
  /** Which credential listed the repositories; 'none' when the project has
   * no repository access yet, null while the first answer is loading. */
  source: ProjectRepositories['source'] | null;
  account: string;
}

/**
 * useProjectRepositories feeds the shared repository selector once a
 * project exists (CO-73): the repositories the project's own repository
 * access can read — its stored Git connection, else the organization's
 * GitHub App — listed by the API. The credential never reaches the browser,
 * and a repository picked here is one a deployment can check out with the
 * same credential.
 */
export function useProjectRepositories(projectId: string): ProjectRepositorySource {
  const [provider, setProvider] = useState<GitProvider>('github');
  const [source, setSource] = useState<ProjectRepositories['source'] | null>(null);
  const [account, setAccount] = useState('');
  const [repositories, setRepositories] = useState<RepositoryItem[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const [repoListError, setRepoListError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadingRepos(true);
    setRepoListError(null);
    api
      .listProjectRepositories(projectId)
      .then((res) => {
        if (cancelled) return;
        // An answer without a source has no repository access to offer.
        setSource(res.source ?? 'none');
        setProvider(res.provider ?? 'github');
        setAccount(res.account ?? '');
        setRepositories(toItems(res.repositories));
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setRepositories([]);
        setRepoListError(cause instanceof Error ? cause.message : 'The repositories could not be listed');
      })
      .finally(() => {
        if (!cancelled) setLoadingRepos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, reloadKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);
  return { provider, source, account, repositories, loadingRepos, repoListError, reload };
}
